/**
 * SessionSlice — debate session lifecycle, AbortController custody, SSE bootstrap.
 *
 * Invariants:
 *   • start() requires a valid UserGestureToken; AudioContext is created
 *     synchronously inside that gesture frame via audioSlice.ensureAudioCtx.
 *   • `maxRounds` is the negotiated per-session round cap (clamped to
 *     [2..50]) — every round-counter UI must read it from here, not a
 *     hard-coded constant.
 *   • `finalizeReason` is set exclusively by the `debate.finalize` SSE
 *     event so the UI can explain WHY a debate ended early
 *     (consensus / deadlock / user cancel).
 *   • `resetForNew` is the canonical "wipe state" entry point — every
 *     post-finalize CTA flows through it.
 */
import type { StateCreator } from "zustand";
import {
  AudioGestureRequiredError,
  isValidUserGestureToken,
  NeedsClarificationError,
  type DebateEvent,
  type DebateTopology,
  type SessionStatus,
  type UserGestureToken,
} from "../../domain/types";
import type { CritiqueStrictness, Urgency } from "../../domain/wizard";
import { generatePersonas } from "../../domain/personas";
import { adaptReplayCore } from "../../domain/replayAdapter";
import { fetchDebateReplay } from "../../transport/authClient";
import { openDebateStream, reattachDebateStream } from "../../transport/sseClient";
import type { DebateStore } from "../debateStore";

export interface DebateStartOptions {
  /** Persona UUIDs from the wizard endpoint; reuses them server-side. */
  personas?: string[];
  /** Override the per-session round cap (clamped server-side to 2..50). */
  maxRounds?: number;
  /** When false, ignore consensus-based early-exit. */
  autoFinalize?: boolean;
  /** Tightness of the critique-first retry budget. */
  critiqueStrictness?: CritiqueStrictness;
  /**
   * Konsey Cognitive Renaissance — Bölüm A0/A1.
   * Erken sonlanma profili: loose | balanced | strict. Backend
   * `DebateEngineOptions.termination_profile` üzerinden alınır.
   */
  terminationProfile?: "loose" | "balanced" | "strict";
  /** Görev 16 precursor — public retrieval backends must stay off. */
  privateMode?: boolean;
  /** Görev 4 — urgency override; omit to let the backend heuristic decide. */
  urgency?: Urgency | null;
  /** Görev 5 — structured decision constraints (budget/timeline/risk/…). */
  constraints?: Record<string, string>;
  /**
   * Persona Kütüphanesi — kalıcı takım id'si. Backend üyeleri koltuk
   * sırasına göre materialise eder; her üye kendi hafızası, öğrenilmiş
   * dersleri ve stil profiliyle masaya oturur.
   */
  libraryTeamId?: string | null;
  /**
   * Organizasyon birimi id'si. Birimdeki TÜM personalar (alt birimler dahil)
   * konuşlandırılır — "Hukuk Departmanı ile tartış" senaryosu.
   */
  libraryUnitId?: string | null;
  /** Takım dışında tek tek seçilmiş kalıcı persona id'leri. */
  libraryPersonaIds?: string[];
  /**
   * Görev 5 — answers to a prior 409's `clarifying_questions`, keyed by
   * the exact question text. Presence alone is enough to unblock the
   * gate server-side; the backend folds them into `constraints`.
   */
  clarificationAnswers?: Record<string, string>;
  /** Görev 5 — proceed despite needs_clarification (operator dismissed the prompt). */
  skipClarification?: boolean;
  /**
   * Konsey Yönergesi — bu tartışmaya özel çalışma talimatı. Backend her
   * personanın sistem prompt'una en yüksek öncelikli blok olarak koyar ve
   * hesap genelindeki Ayarlar prompt'uyla çelişirse buradaki kazanır.
   */
  userDebatePrompt?: string | null;
}

/** Görev 5 — surfaced when `POST /api/debate` returns 409 needs_clarification. */
export interface ClarificationPrompt {
  questions: string[];
  frame: Record<string, unknown> | null;
}

export interface SessionTransportConfig {
  endpoint: string;
  onEvent: (evt: DebateEvent) => void;
  onError?: (err: Error) => void;
  headers?: Record<string, string>;
}

export interface SessionSlice {
  sessionId: string | null;
  /**
   * Pre-debate staging id used by the RAG uploader (and any other
   * pre-flight resource that must be addressable BEFORE `start()` mints
   * the live `sessionId`).
   *
   * Lifecycle:
   *   1. Wizard mounts → null.
   *   2. User drops a file in `RagDropZone` → `ensurePendingSessionId()`
   *      mints a UUID and writes it here. The backend persists the
   *      uploaded blob keyed on this id.
   *   3. User clicks "Tartışmayı Başlat" → `start()` ADOPTS this id as
   *      the canonical `sessionId` (so the RAG documents the wizard
   *      uploaded are visible to the engine) and clears
   *      `pendingSessionId` to `null`.
   *   4. `resetForNew()` also clears it — a hard wipe must drop staged
   *      uploads alongside everything else.
   *
   * This field IS persisted (unlike `sessionId`, which is intentionally
   * NOT persisted to avoid the stuck-verdict bug on tab reload). That's
   * deliberate: if the user uploads docs, refreshes, and comes back, the
   * staged docs should still be reachable from the new session start.
   */
  pendingSessionId: string | null;
  /**
   * How many council-pool documents are attached to the id this debate
   * will actually run under.
   *
   * Uploads are staged against an id minted before the debate exists, and
   * every break in that hand-off silently detaches them: the files are
   * indexed, the dropzone in step 2 still lists them, and the council
   * nevertheless reads nothing. Surfacing the count at the launch button
   * turns that failure from invisible into obvious — an operator who
   * uploaded eighteen files and sees "0 belge" knows before spending ten
   * minutes on a debate built from general market commentary.
   */
  stagedDocCount: number;
  query: string;
  topology: DebateTopology;
  status: SessionStatus;
  startedAt: number | null;
  /**
   * The effective per-session round cap negotiated at start time. Always
   * inside [WIZARD_LIMITS.maxRoundsMin..maxRoundsMax]. Read by the
   * AppHeader / ModeratorBar / RoundtableStage round counters so we
   * never display stale "4/8" when the user picked 4.
   */
  maxRounds: number;
  /**
   * Why the debate ended. Populated by the `debate.finalize` SSE event.
   *   - "consensus" — agents converged before the cap (early, success).
   *   - "deadlock"  — consensus oscillated / cap hit (early, stuck).
   *   - "user"      — operator cancelled.
   *   - null        — still running or no signal yet.
   *
   * Read by the round counter so a "4 / 5 — Konsensüs" pill can explain
   * WHY the table stopped before reaching the user-set cap, instead of
   * leaving the operator wondering whether something crashed.
   */
  // Konsey Cognitive Renaissance — Bölüm A1.
  // `max_rounds` reason yeni: kullanıcının tur bütçesi tükendi ama
  // gerçek consensus / deadlock olmadı. FE pill rengi ayrı.
  finalizeReason:
    | "consensus"
    | "deadlock"
    | "user"
    | "max_rounds"
    | null;
  abort: AbortController | null;
  error: Error | null;
  transport: SessionTransportConfig | null;
  /**
   * True while the SSE client is waiting between reconnect attempts.
   * Lets the UI surface a "Yeniden bağlanıyor…" banner without
   * changing the debate's `status` field (which drives orchestration logic).
   */
  reconnecting: boolean;
  /** Which reconnect attempt is in progress (1-based). */
  reconnectAttempt: number;
  /**
   * Görev 5 — non-null while a `start()` call is blocked on a material-
   * ambiguity clarification gate (backend 409). The wizard renders
   * `ClarificationModal` when this is set; resubmitting via `start()`
   * with `clarificationAnswers` or `skipClarification` clears it.
   */
  clarification: ClarificationPrompt | null;

  setQuery(q: string): void;
  setTopology(t: DebateTopology): void;
  configureTransport(cfg: SessionTransportConfig): void;
  setStatus(s: SessionStatus): void;
  setError(e: Error | null): void;
  setFinalizeReason(
    reason: "consensus" | "deadlock" | "user" | "max_rounds" | null,
  ): void;
  /** Görev 5 — set/clear the pending clarification prompt. */
  setClarification(prompt: ClarificationPrompt | null): void;
  /** Mint a session id during the wizard so RAG uploads can attach. */
  ensurePendingSessionId(): string;
  /** Publish the council-pool document count for the launch surface. */
  setStagedDocCount(count: number): void;
  start(
    query: string,
    topology: DebateTopology,
    gesture: UserGestureToken,
    opts?: DebateStartOptions,
  ): Promise<void>;
  /**
   * İş Kolu — arka planda devam eden tartışma.
   *
   * Re-enter an EXISTING session (typically one the user started, closed
   * the tab on, and is now reopening from the "Devam Eden" dashboard
   * filter). Unlike `start()`, this NEVER mints a new session id and
   * NEVER re-admits against the backend's concurrency cap — it:
   *   1. Hydrates the transcript-so-far from the DB-backed
   *      `GET /api/debate/history/{id}` (works for ANY status).
   *   2. If that session is still `running` server-side, attaches a
   *      LIVE SSE tail via `GET /api/debate/{id}/stream` — the SAME
   *      background `engine_task` the original `start()` call booted,
   *      still running because closing a tab no longer cancels it
   *      (see backend `routes_debate.py`).
   *   3. Otherwise (finalized/interrupted/error), just renders the
   *      static transcript — no stream is opened.
   */
  resume(sessionId: string): Promise<void>;
  cancel(reason?: string): void;
  /**
   * Hard reset back to a clean `draft` state.
   *
   * Aborts any in-flight stream, drops the current session id, clears
   * the transcript / verdict / consensus / interjections, and (unless
   * `keepWizard`) wipes the wizard configuration too. Powers every
   * post-finalize CTA: "Tekrar Çalıştır" / "Yeni Tartışma" / "Düzenle".
   */
  resetForNew(opts?: { keepWizard?: boolean }): void;
}

export type SessionSliceCreator = StateCreator<
  DebateStore,
  [["zustand/immer", never]],
  [],
  SessionSlice
>;

function freshSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `sess-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export const createSessionSlice: SessionSliceCreator = (set, get) => ({
  sessionId: null,
  pendingSessionId: null,
  stagedDocCount: 0,
  query: "",
  topology: "round-robin",
  status: "draft",
  startedAt: null,
  maxRounds: 5,
  finalizeReason: null,
  abort: null,
  error: null,
  transport: null,
  reconnecting: false,
  reconnectAttempt: 0,
  clarification: null,

  setClarification: (prompt) => {
    set((state) => {
      state.clarification = prompt;
    });
  },

  setQuery: (q) => {
    set((state) => {
      state.query = q;
    });
  },

  setTopology: (t) => {
    set((state) => {
      state.topology = t;
    });
  },

  configureTransport: (cfg) => {
    set((state) => {
      state.transport = cfg;
    });
  },

  setStatus: (s) => {
    set((state) => {
      state.status = s;
    });
  },

  setError: (e) => {
    set((state) => {
      state.error = e;
      if (e) state.status = "error";
    });
  },

  setFinalizeReason: (reason) => {
    set((state) => {
      state.finalizeReason = reason;
    });
  },

  ensurePendingSessionId: () => {
    // Prefer an in-flight `sessionId` (RAG uploads happening DURING a
    // running debate go to the live session). Otherwise mint a staging
    // id into `pendingSessionId` so pre-debate uploads are addressable
    // before `start()` runs. Critical: we no longer mutate `sessionId`
    // here — that field is the sole property of `start()` and the SSE
    // `persona.hydrate` event, never the wizard.
    const live = get().sessionId;
    if (live) return live;
    const pending = get().pendingSessionId;
    if (pending) return pending;
    const id = freshSessionId();
    set((state) => {
      state.pendingSessionId = id;
    });
    return id;
  },

  setStagedDocCount: (count) => {
    set((state) => {
      state.stagedDocCount = Math.max(0, Math.floor(count));
    });
  },

  start: async (query, topology, gesture, opts) => {
    if (!isValidUserGestureToken(gesture)) {
      throw new AudioGestureRequiredError(
        "sessionSlice.start: missing or expired UserGestureToken.",
      );
    }
    const transport = get().transport;
    if (!transport) {
      throw new Error(
        "sessionSlice.start: transport not configured. Call configureTransport(...) first.",
      );
    }

    // Synchronous AudioContext provisioning inside the gesture frame.
    get().ensureAudioCtx(gesture);

    // -----------------------------------------------------------------
    // FRESH-SESSION INVARIANT (HARD)
    // -----------------------------------------------------------------
    // Every `start()` call mints a brand-new session_id and wipes any
    // residual session-scoped state, regardless of the previous status.
    //
    // Why this is hard-reset (not status-conditional):
    //   • The persisted Zustand snapshot rehydrates `sessionId` on every
    //     page load. After a finalized run the user reloads the SPA, the
    //     status drops back to its default "draft" (status is NOT
    //     persisted), but `sessionId` carries over silently. Reusing it
    //     against a fresh `start()` made the backend either return the
    //     cached verdict (→ instant "verdict.ready" → status flips
    //     straight to "finalized" → stuck "SONUÇLANDI" pill) or reject
    //     the request because the row already exists. Both produce the
    //     "Tartışmayı Başlat dedim bi bok olmadı" symptom.
    //   • There is no UX surface for "resume previous session" — the
    //     wizard always starts a fresh debate, so reusing IDs has zero
    //     product value and 100% bug surface.
    //
    // We also abort any stray AbortController the previous run left
    // behind so its async cleanup can't write to the new session.
    {
      const stale = get().abort;
      if (stale && !stale.signal.aborted) {
        stale.abort("session-restart");
      }
      set((state) => {
        state.sessionId = null;
        state.status = "draft";
        state.error = null;
        state.startedAt = null;
        state.finalizeReason = null;
        state.abort = null;
        // Görev 5 — a brand-new `start()` call always supersedes any
        // pending clarification prompt from a previous attempt on this
        // same session; the resubmit path below re-populates it if the
        // (now-answered) query is STILL materially ambiguous.
        state.clarification = null;
        state.seatOrder = [];
        state.personas = {};
        state.turnOrder = [];
        state.turnsById = {};
        state.currentTurnId = null;
        state.currentSpeakerId = null;
        state.previousSpeakerId = null;
      });
      get().resetStream();
      get().resetModerator();
      get().clearVerdict();
      if (typeof get().resetInterjections === "function") {
        get().resetInterjections();
      }
      if (typeof get().resetQuality === "function") {
        get().resetQuality();
      }
    }

    // Persona Kütüphanesi — kalıcı bir konsey seçildiyse koltukları BURADA
    // doldurmuyoruz. Kimlikler backend'de materialise edilir (her biri YENİ
    // bir runtime uuid alır) ve `persona.hydrate` ile gelir.
    //
    // BULUNAN HATA: bu dal olmadan aşağıdaki `generatePersonas()` fallback'i
    // devreye giriyor, ürettiği DETERMİNİSTİK uuid'ler `personas` alanında
    // sunucuya gidiyor ve engine `preset personas missing in DB` ile
    // çöküyordu — çünkü o satırlar DB'de hiç yok. Kütüphane yolunda
    // `personas` alanı hiç gönderilmemeli.
    const usingLibrary = Boolean(
      opts?.libraryTeamId ||
        opts?.libraryUnitId ||
        (opts?.libraryPersonaIds?.length ?? 0) > 0,
    );

    let seatOrder: string[];
    if (usingLibrary) {
      get().resetPersonas();
      seatOrder = [];
    } else if (opts?.personas && opts.personas.length > 0) {
      const wizPersonas = get().wizard.personas;
      if (wizPersonas.length > 0) {
        get().hydratePersonas({
          personas: wizPersonas,
          seatOrder: opts.personas,
        });
        seatOrder = opts.personas;
      } else {
        const det = generatePersonas(query, { topology });
        get().hydratePersonas(det);
        seatOrder = det.seatOrder;
      }
    } else {
      const det = generatePersonas(query, { topology });
      get().hydratePersonas(det);
      seatOrder = det.seatOrder;
    }
    get().resetStream();
    get().resetModerator();
    get().clearVerdict();
    // Drop any prior interjection state — a brand-new session must
    // never inherit the previous council's queued / applied messages.
    if (typeof get().resetInterjections === "function") {
      get().resetInterjections();
    }
    // Same for the quality-plan overlays (coverage/graph/verify/plan/
    // summons/subagents) — stale badges on a fresh council are worse
    // than no badges.
    if (typeof get().resetQuality === "function") {
      get().resetQuality();
    }

    // ADOPT the wizard's `pendingSessionId` if one exists. This is the
    // ONLY non-fresh path — and it's safe because `pendingSessionId` is
    // a wizard-staging artefact (RAG uploads), NOT a previous debate's
    // live id. The wizard has no reason to mint one unless the user
    // dragged a file into `RagDropZone`; reusing it here means those
    // documents flow into the engine on the very first turn.
    //
    // Falls back to a fresh UUID when no docs were staged. We also wipe
    // the staging slot so a follow-up wizard run mints a clean one.
    const adopted = get().pendingSessionId;
    const sessionId = adopted ?? freshSessionId();
    const controller = new AbortController();

    // Effective round cap for this session — MUST mirror the server's
    // capping logic so the UI never overshoots. Server hard cap is 50
    // (`Settings.hard_cap_rounds`); minimum is 2 to allow at least one
    // rebuttal round. The literal upper bound was previously stuck at
    // 8, which masked user choices > 8 and printed "4/8" when the user
    // selected 4 rounds at high agentCount counts.
    const effectiveMaxRounds = Math.max(
      2,
      Math.min(50, Math.round(opts?.maxRounds ?? get().maxRounds ?? 5)),
    );

    set((state) => {
      state.query = query;
      state.topology = topology;
      state.sessionId = sessionId;
      // Wipe the staging slot — its contents (RAG docs) just got
      // promoted to the live session above. Leaving it set would let a
      // follow-up wizard run silently re-attach to the prior debate.
      state.pendingSessionId = null;
      state.status = "provisioning";
      state.startedAt = Date.now();
      state.maxRounds = effectiveMaxRounds;
      // Clear any prior finalize reason so the new session's "running"
      // pill doesn't carry over a stale "Konsensüs" / "Deadlock" badge.
      state.finalizeReason = null;
      state.abort = controller;
      state.error = null;
    });

    try {
      // Transition to running before opening the stream so UI can show the
      // roundtable while the first SSE frame is in flight.
      set((state) => {
        state.status = "running";
      });

      const requestBody: Record<string, unknown> = {
        sessionId,
        query,
        topology,
      };
      // Boş `personas` göndermek backend'de `preset_uuids = []` üretir ve
      // engine'i "hiç persona yok" yoluna sokar; kütüphane yolunda alan
      // TAMAMEN atlanmalı ki engine koltukları materialise edilmiş
      // kimliklerden kursun.
      if (seatOrder.length > 0) {
        requestBody["personas"] = seatOrder;
      }
      // Görev 3-5 — private mode / urgency / structured constraints /
      // clarification resubmission. All optional; omitted keys let the
      // backend's heuristics decide (see `routes_debate.start_debate`).
      if (opts?.privateMode) {
        requestBody["private_mode"] = true;
      }
      if (opts?.urgency) {
        requestBody["urgency"] = opts.urgency;
      }
      if (opts?.constraints && Object.keys(opts.constraints).length > 0) {
        requestBody["constraints"] = opts.constraints;
      }
      if (opts?.clarificationAnswers && Object.keys(opts.clarificationAnswers).length > 0) {
        requestBody["clarification_answers"] = opts.clarificationAnswers;
      }
      if (opts?.skipClarification) {
        requestBody["skip_clarification"] = true;
      }
      // Send the FULL persona objects alongside the seat-order UUIDs so
      // the backend can pre-flight upsert them into `macdm.personas`
      // BEFORE the engine boots. This neutralises a critical race that
      // surfaced in production at concurrency: the wizard's lazy persist
      // would still be in-flight when the user clicked "Start", and the
      // engine would crash with `preset personas missing in DB: {…}`.
      // With snapshots embedded the start flow is fully self-contained.
      const wizardPersonas = get().wizard.personas;
      if (wizardPersonas.length > 0) {
        requestBody["persona_snapshots"] = wizardPersonas;
      }
      // Persona Kütüphanesi — kalıcı kimlikler AYRI bir yoldan gider.
      // `personas`/`persona_snapshots` geçici (wizard üretimi) koltukları
      // taşır; bunlar ise backend'in kütüphaneden materialise edip
      // hafıza/ders/stil katmanlarıyla birlikte kuracağı kimliklerdir.
      if (opts?.libraryTeamId) {
        requestBody["team_id"] = opts.libraryTeamId;
      }
      if (opts?.libraryUnitId) {
        requestBody["unit_id"] = opts.libraryUnitId;
      }
      if (opts?.libraryPersonaIds && opts.libraryPersonaIds.length > 0) {
        requestBody["library_persona_ids"] = opts.libraryPersonaIds;
      }
      if (typeof opts?.maxRounds === "number") {
        requestBody["max_rounds"] = opts.maxRounds;
      }
      if (typeof opts?.autoFinalize === "boolean") {
        requestBody["auto_finalize"] = opts.autoFinalize;
      }
      if (opts?.critiqueStrictness) {
        requestBody["critique_strictness"] = opts.critiqueStrictness;
      }
      // Konsey Cognitive Renaissance — Bölüm A0/A1.
      if (opts?.terminationProfile) {
        requestBody["termination_profile"] = opts.terminationProfile;
      }
      if (opts?.userDebatePrompt && opts.userDebatePrompt.trim().length > 0) {
        requestBody["user_debate_prompt"] = opts.userDebatePrompt.trim();
      }
      await openDebateStream({
        url: transport.endpoint,
        body: requestBody,
        ...(transport.headers ? { headers: transport.headers } : {}),
        signal: controller.signal,
        onEvent: transport.onEvent,
        onError: (err) => {
          // `sseClient` invokes `onError` for EVERY rejection it throws,
          // including `NeedsClarificationError` — which is explicitly NOT
          // a transport failure (see its class doc). Forwarding it into
          // `setError` here fired the generic "Akış başarısız oldu" toast
          // in lockstep with the (correct) `ClarificationModal` render
          // from this function's own catch block below, drowning out the
          // modal with a spurious error banner on every clarification
          // gate hit. The typed catch below is the ONLY place allowed to
          // react to this error type.
          if (err instanceof NeedsClarificationError) return;
          transport.onError?.(err);
          get().setError(err);
        },
        // Surface reconnect attempts as a non-blocking UI signal.
        onReconnecting: (attempt, maxAttempts, delayMs) => {
          // eslint-disable-next-line no-console
          console.warn(
            `[session] SSE reconnecting — attempt ${attempt}/${maxAttempts}, ` +
            `waiting ${delayMs}ms`,
          );
          set((state) => {
            state.reconnecting = true;
            state.reconnectAttempt = attempt;
          });
          // Clear the banner once the delay has elapsed (best-effort).
          setTimeout(() => {
            set((state) => {
              state.reconnecting = false;
            });
          }, delayMs + 200);
        },
      });

      // openDebateStream resolves when the server closes the SSE channel.
      // We must distinguish between three close cases:
      //   1. Clean finish — verdict SSE already set status="finalized".
      //   2. Mid-debate drop with transcript but no verdict — reconnect or
      //      surface graceful error so user can decide.
      //   3. Empty drop (0 turns, 0 verdict) — surface error immediately.
      const finalState = get();
      if (finalState.status === "running") {
        if (finalState.verdict) {
          // Case 1: verdict already arrived — promote.
          set((state) => { state.status = "finalized"; });
        } else if (finalState.turnOrder.length > 0) {
          // Case 2: partial transcript but no verdict — stream dropped
          // mid-debate (ERR_QUIC / 503 after retries exhausted).
          // openDebateStream already exhausted its internal retry budget;
          // surface a recoverable error so the UI can offer manual reconnect.
          // eslint-disable-next-line no-console
          console.warn(
            "[start] stream dropped mid-debate after retry exhaustion — " +
            "partial transcript available, surfacing recoverable error",
          );
          set((state) => {
            state.status = "error";
            state.reconnecting = false;
            state.error = Object.assign(
              new Error(
                "Bağlantı geçici olarak kesildi. Yeniden bağlanmak için " +
                "'Yeniden Dene' düğmesine basın.",
              ),
              { recoverable: true },
            );
          });
        } else {
          // Case 3: empty drop — no useful state.
          // eslint-disable-next-line no-console
          console.warn(
            "[start] stream closed without verdict or turns — surfacing as error",
          );
          set((state) => {
            state.status = "error";
            state.reconnecting = false;
            state.error = new Error(
              "Konsey üretici akışı boş kapandı. Lütfen yeniden başlatın.",
            );
          });
        }
      }
      // Ensure reconnecting banner is cleared on any clean exit.
      set((state) => { state.reconnecting = false; });
    } catch (err) {
      if (controller.signal.aborted) {
        // Cancellation path — status was already set by cancel().
        set((state) => { state.reconnecting = false; });
        return;
      }
      // Görev 5 — needs_clarification is a SOFT stop, not a failure:
      // the request never admitted a session server-side. Revert to
      // "draft" (never "error") and surface the questions so the
      // wizard can render `ClarificationModal` instead of the generic
      // error banner. Swallowed here — NOT re-thrown — so callers
      // (`useStartDebate`, `SetupWizard.onStart`) don't have to special-
      // case this in every call site.
      if (err instanceof NeedsClarificationError) {
        set((state) => {
          state.reconnecting = false;
          state.status = "draft";
          state.clarification = { questions: err.questions, frame: err.frame };
        });
        return;
      }
      set((state) => { state.reconnecting = false; });
      const e = err instanceof Error ? err : new Error(String(err));
      get().setError(e);
      throw e;
    }
  },

  resume: async (sessionId) => {
    const transport = get().transport;
    if (!transport) {
      throw new Error(
        "sessionSlice.resume: transport not configured. Call configureTransport(...) first.",
      );
    }

    // Same hygiene as start(): a stray controller from a previous
    // session/tab must never write into the session we're about to hydrate.
    {
      const stale = get().abort;
      if (stale && !stale.signal.aborted) {
        stale.abort("session-restart");
      }
    }

    // 1) Hydrate transcript-so-far from the DB — the ONLY backfill source.
    // The live channel (step 2 below) never replays already-emitted
    // events; it only carries frames from the moment of attachment on.
    const replay = await fetchDebateReplay(sessionId);
    const core = adaptReplayCore(replay);

    get().resetStream();
    get().resetModerator();
    get().clearVerdict();
    if (typeof get().resetInterjections === "function") {
      get().resetInterjections();
    }
    if (typeof get().resetQuality === "function") {
      get().resetQuality();
    }
    get().hydratePersonas({
      personas: core.personas,
      seatOrder: core.personas.map((p) => p.id),
    });

    // Bilişsel iz — `brain.update` SSE'si yalnızca bağlandığı andan
    // sonrasını taşır, yani resume edilen bir oturumda o ana kadarki
    // snapshot'lar asla gelmez ve "Bilişsel Profil" paneli boş kalırdı.
    // Transkript nasıl DB'den geri yükleniyorsa beyin izi de öyle.
    if (typeof get().resetBrain === "function") {
      get().resetBrain();
      for (const list of Object.values(replay.brain_snapshots ?? {})) {
        for (const snap of list) {
          get().applyBrainSnapshot(snap);
        }
      }
    }

    set((state) => {
      state.sessionId = sessionId;
      state.pendingSessionId = null;
      state.query = core.query;
      state.topology = core.topology;
      state.startedAt = core.startedAt ?? Date.now();
      state.maxRounds = core.maxRounds ?? state.maxRounds;
      state.error = null;
      state.finalizeReason = null;
      state.clarification = null;
    });

    // Replay turns through the SAME actions the live SSE dispatcher uses
    // (see `useDiscussionStream.ts`'s `turn.start`/`turn.critique`/
    // `turn.end` handling) so a resumed transcript renders through the
    // identical code path as a live one — no parallel rendering logic.
    for (const t of core.turns) {
      get().startTurn({
        id: t.id,
        speakerId: t.speakerId,
        roundIndex: t.roundIndex,
        phase: "yielding",
        critique: null,
        startedAt: t.startedAt,
      });
      if (t.critique) {
        get().attachCritique(t.id, t.critique);
      }
      get().endTurn(t.id, t.finalText ?? "", t.outcome ?? "spoke", t.forfeitReason);
    }
    if (core.consensusScore !== undefined) {
      get().pushConsensus({
        round: core.turns.length > 0 ? core.turns[core.turns.length - 1]!.roundIndex : 0,
        agreementMatrix: [],
        scalarScore: core.consensusScore,
        deadlock: false,
        shouldFinalize: false,
      });
    }
    if (core.verdict) {
      get().setVerdict(core.verdict);
    }

    if (core.status !== "running" && core.status !== "provisioning") {
      // Terminal — no engine left to reattach to. Static render only.
      set((state) => {
        state.status =
          core.status === "interrupted" || core.status === "error"
            ? (core.status as SessionStatus)
            : "finalized";
      });
      return;
    }

    // 2) Live — attach the SSE tail. Optimistic "running"; `onNotLive`
    // demotes it if the engine actually finished in the gap between our
    // DB read above and this call (harmless, inherent race — the DB read
    // already captured everything that happened up to that point).
    const controller = new AbortController();
    set((state) => {
      state.abort = controller;
      state.status = "running";
    });

    try {
      await reattachDebateStream({
        url: `${transport.endpoint}/${sessionId}/stream`,
        ...(transport.headers ? { headers: transport.headers } : {}),
        signal: controller.signal,
        onEvent: transport.onEvent,
        onNotLive: () => {
          set((state) => {
            state.status = "interrupted";
          });
        },
        onError: (err) => {
          transport.onError?.(err);
          get().setError(err);
        },
        onReconnecting: (attempt, _maxAttempts, delayMs) => {
          set((state) => {
            state.reconnecting = true;
            state.reconnectAttempt = attempt;
          });
          setTimeout(() => {
            set((state) => {
              state.reconnecting = false;
            });
          }, delayMs + 200);
        },
      });
      set((state) => {
        state.reconnecting = false;
      });
    } catch (err) {
      set((state) => {
        state.reconnecting = false;
      });
      if (controller.signal.aborted) {
        return;
      }
      const e = err instanceof Error ? err : new Error(String(err));
      get().setError(e);
      throw e;
    }
  },

  cancel: (reason) => {
    const controller = get().abort;
    if (controller && !controller.signal.aborted) {
      controller.abort(reason ?? "user-cancelled");
    }
    set((state) => {
      state.abort = null;
      state.status = state.status === "error" ? "error" : "interrupted";
    });
  },

  resetForNew: (opts) => {
    // Belt-and-suspenders: abort any live stream first.
    const controller = get().abort;
    if (controller && !controller.signal.aborted) {
      controller.abort("reset-for-new");
    }
    // Re-use existing slice resets so we don't drift over time.
    get().resetStream();
    get().resetModerator();
    get().clearVerdict();
    if (typeof get().resetInterjections === "function") {
      get().resetInterjections();
    }
    if (typeof get().resetQuality === "function") {
      get().resetQuality();
    }

    set((state) => {
      state.abort = null;
      state.error = null;
      state.sessionId = null;
      // A hard reset ("Yeni Tartışma") must drop the staged RAG id so the
      // next wizard run cannot silently inherit the previous run's
      // documents. `keepWizard` is the opposite intent — the operator is
      // stepping BACK into the wizard they were already filling in — and
      // wiping the staging id there stranded every file they had already
      // uploaded in a session no debate would ever read. That is how one
      // account accumulated 77 uploaded documents that no council saw.
      if (opts?.keepWizard !== true) {
        state.pendingSessionId = null;
        state.stagedDocCount = 0;
      }
      state.status = "draft";
      state.startedAt = null;
      state.finalizeReason = null;
      state.reconnecting = false;
      state.reconnectAttempt = 0;
      state.clarification = null;
      // Wipe the live transcript / persona snapshot so the next run
      // doesn't visually inherit the previous council.
      state.seatOrder = [];
      state.personas = {};
      state.turnOrder = [];
      state.turnsById = {};
      state.currentTurnId = null;
      state.currentSpeakerId = null;
      state.previousSpeakerId = null;
    });

    if (opts?.keepWizard !== true) {
      get().resetWizard();
    }
  },
});
